import { useTheme } from '@mui/material';
import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { useEffect, useMemo } from 'react';
import { CircleLayer, Layer, MapRef, Source } from 'react-map-gl';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
// import { selectCurrHighlightedColorBucket } from '../../cube/cubeSlice';
import { Expression } from 'mapbox-gl';
import { selectCurrHiddenColorBuckets } from '../../../services/data/impl/cube/cubeSlice';
import {
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import { useBlueberriesMeanStdDev } from '../../scanDataApi/hooks/useBlueberryDistributionByClass';
import { useGetIntervalAndMeasure } from '../../scanDataApi/hooks/useGetIntervalAndMeasure';
import useScanData from '../../scanDataApi/hooks/useScanData';
import {
  ScanDataEnum,
  ScanDataMeasureEnum,
} from '../../scanDataApi/scanDataDefinitions';
import {
  selectCurrBlockID,
  selectCurrBlueberryClass,
  selectCurrFeature,
  selectCurrMeasure,
  selectCurrPointId,
  selectCurrRowsideId,
  selectHoverPointId,
  selectHoverRowsideId,
} from '../../select/selectSlice';
import { LayerExistenceDict } from '../hooks/useCheckLayers';
import useLayerOrder from '../hooks/useLayerOrder';
import {
  LayerEnum,
  MAX_ZOOM,
  MapZoomModeEnum,
  selectHSVSaturation,
  selectHSVValue,
  selectLayerVisibility,
  selectUseCustomHSVSaturation,
  selectUseCustomHSVValue,
  selectZoomMode,
  setPointsViewBounds,
} from '../mapSlice';
import {
  animatePointHighlight,
  createPointFeaturesFromBlueberriesData,
  createPointFeaturesFromScanData,
  ensureLayerExists,
} from '../mapUtils';
import { EMPTY_FEATURE_COLLECTION } from '../utils/constants';
import { createFeatureCollection } from '../utils/createFeatureCollection';
import { getBoundsFromFeatureCollection } from '../utils/getBoundsFromFeatureCollection';

export const pointRadiusAtStartZoomLevel = 4;
export const pointRadiusAtEndZoomLevel = 8;
// Define zoom levels where you want the radius to adjust
const startZoomLevel = 16;
const endZoomLevel = MAX_ZOOM;
export const fadeOutExtraCircleRadius = 30;
export const fadeInDuration = 300; // give the fade-in animation a short delay bc otherwise previous point might also get highlighted
export const fadeOutDuration = 1000;

export type GeoJsonDataType = FeatureCollection<Geometry, GeoJsonProperties>;

interface Props {
  mapRef: MapRef | null;
  layerExistenceDict: LayerExistenceDict;
}

const PointDataLayers: React.FC<Props> = ({ mapRef, layerExistenceDict }) => {
  const theme = useTheme();
  const { mapPoints, standardDeviation, mean, blueberryData } = useScanData();
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const currBlockID = useAppSelector(selectCurrBlockID);
  const currFeature = useAppSelector(selectCurrFeature);
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currRowsideId = useAppSelector(selectCurrRowsideId);
  const currPointId = useAppSelector(selectCurrPointId);
  const hoverRowsideId = useAppSelector(selectHoverRowsideId);
  const hoverPointId = useAppSelector(selectHoverPointId);
  const HSVSaturation = useAppSelector(selectHSVSaturation);
  const HSVValue = useAppSelector(selectHSVValue);
  const useCustomHSVSaturation = useAppSelector(selectUseCustomHSVSaturation);
  const useCustomHSVValue = useAppSelector(selectUseCustomHSVValue);
  // const currHighlightedBucket = useAppSelector(selectCurrHighlightedColorBucket)
  const currHiddenColorBuckets = useAppSelector(selectCurrHiddenColorBuckets);
  const layerBeforeId = useLayerOrder(LayerEnum.heatmap, layerExistenceDict);
  const layerBeforeIdCurrPoint = useLayerOrder(
    LayerEnum.heatmapCurrentPoint,
    layerExistenceDict
  );
  const currBlueberryClass = useAppSelector(selectCurrBlueberryClass);
  const { measure } = useGetIntervalAndMeasure();
  const zoomMode = useAppSelector(selectZoomMode);
  const dispatch = useAppDispatch();

  const {
    data: stdDevMeanData,
    isLoading: stdDevLoading,
    error: stdDevError,
  } = useBlueberriesMeanStdDev();

  const isDisabled =
    layerVisibility[LayerEnum.heatmap] === false ||
    currBlockID === '' ||
    blueberryData.isLoading ||
    blueberryData.error ||
    mapPoints.isLoading ||
    mapPoints.error ||
    stdDevLoading ||
    stdDevError ||
    isBlueberryFeature(currFeature) ||
    isBlueberryFlowerFeature(currFeature)
      ? blueberryData.data == null || stdDevMeanData == null
      : mapPoints.data == null;

  const pointFeatureCollection = useMemo(() => {
    if (isDisabled) {
      return EMPTY_FEATURE_COLLECTION;
    }

    // handle if current feature is blueberry case and use different points
    // generation
    const pointFeatures = isBlueberryFeature(currFeature)
      ? createPointFeaturesFromBlueberriesData(
          blueberryData.data,
          currBlueberryClass,
          stdDevMeanData?.mean || null,
          stdDevMeanData?.std_dev || null,
          measure as string
        )
      : createPointFeaturesFromScanData(
          mapPoints.data,
          measure as ScanDataMeasureEnum,
          standardDeviation.data,
          mean.data,
          useCustomHSVSaturation,
          useCustomHSVValue,
          HSVSaturation,
          HSVValue
        );

    if (!pointFeatures) {
      return EMPTY_FEATURE_COLLECTION;
    }

    return createFeatureCollection(pointFeatures);
  }, [
    mapPoints.data,
    currMeasure,
    measure,
    standardDeviation.data,
    mean.data,
    isDisabled,
    useCustomHSVSaturation,
    useCustomHSVValue,
    HSVSaturation,
    HSVValue,
  ]);

  const pointBBox = useMemo(() => {
    if (zoomMode === MapZoomModeEnum.pointLevel) {
      return getBoundsFromFeatureCollection(pointFeatureCollection);
    }
    return null;
  }, [pointFeatureCollection, zoomMode === MapZoomModeEnum.pointLevel]);

  useEffect(() => {
    if (zoomMode === MapZoomModeEnum.pointLevel) {
      dispatch(setPointsViewBounds(pointBBox));
    }
  }, [pointBBox, zoomMode === MapZoomModeEnum.pointLevel]);

  const hiddenPointsCriteria = useMemo(() => {
    if (currHiddenColorBuckets.length) {
      return [
        'case',
        ['in', ['get', 'hueValue'], ['literal', currHiddenColorBuckets]],
        0,
        1,
      ] as Expression;
    }

    // if (currBlueberryClass) {
    //   console.log('HERE', currBlueberryClass);
    //   return [
    //     'case',
    //     ['!=', ['get', 'color'], BlueberryColorsMap[currBlueberryClass]],
    //     0,
    //     1,
    //   ] as Expression;
    // }

    return [
      'case',
      ['==', ['get', ScanDataEnum.rowside_id], currRowsideId],
      0.8, // Opacity if current rowside ID matches
      ['==', ['get', ScanDataEnum.rowside_id], hoverRowsideId],
      0.8, // Opacity if hovered rowside ID matches
      ['==', ['get', ScanDataEnum.point_id], currPointId],
      1, // Opacity if current point ID matches
      layerVisibility[LayerEnum.heatmap] ? 1 : 0, // Default opacity based on heatmap layer visibility
    ] as Expression;
  }, [
    // currBlueberryClass,
    currHiddenColorBuckets,
    currRowsideId,
    hoverRowsideId,
    currPointId,
  ]);

  const heatmapLayerStyle = useMemo(() => {
    if (isDisabled) {
      return null;
    } else {
      const layerStyle: CircleLayer = {
        id: LayerEnum.heatmap,
        maxzoom: MAX_ZOOM,
        type: 'circle',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'], // Linear interpolation
            ['zoom'], // Zoom-dependent scaling
            startZoomLevel, // Start zoom level
            [
              'case',
              ['==', ['get', ScanDataEnum.rowside_id], currRowsideId],
              pointRadiusAtStartZoomLevel + 3, // Radius if current rowside ID matches
              ['==', ['get', ScanDataEnum.rowside_id], hoverRowsideId],
              pointRadiusAtStartZoomLevel + 3, // Radius if hovered rowside ID matches
              pointRadiusAtStartZoomLevel, // Default radius based on start zoom level
            ],
            endZoomLevel, // End zoom level
            [
              'case',
              ['==', ['get', ScanDataEnum.rowside_id], currRowsideId],
              pointRadiusAtEndZoomLevel + 1, // Radius if current rowside ID matches
              ['==', ['get', ScanDataEnum.rowside_id], hoverRowsideId],
              pointRadiusAtEndZoomLevel + 1, // Radius if hovered rowside ID matches
              pointRadiusAtEndZoomLevel, // Point radius at end zoom level
            ],
          ],
          'circle-color': ['get', 'color'], // Color of the circle, fetched from data
          // currHighlightedBucket !== null
          'circle-opacity': hiddenPointsCriteria,
        },
      };

      return layerStyle;
    }
  }, [
    isDisabled,
    layerVisibility[LayerEnum.heatmap],
    mapPoints,
    currRowsideId,
    hoverRowsideId,
    hiddenPointsCriteria,
  ]);

  // Highlighted circle layer style for both currently selected point and hovered point
  const highlightedCircleLayerStyle = useMemo(() => {
    if (isDisabled || currPointId === undefined) return null;

    const layerStyle: CircleLayer = {
      id: `${LayerEnum.heatmapCurrentPoint}`,
      maxzoom: MAX_ZOOM,
      type: 'circle',
      filter: [
        'any',
        ['==', ['get', ScanDataEnum.point_id], currPointId], // This filter includes the circle with currPointId
        ['==', ['get', ScanDataEnum.point_id], hoverPointId], // This filter includes the circle with hoverPointId
      ],
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'], // Same zoom-dependent scaling
          startZoomLevel, // Same start zoom level
          pointRadiusAtStartZoomLevel + 5, // Radius of normal points + extra to highlight it
          endZoomLevel, // Same end zoom level
          pointRadiusAtEndZoomLevel + 5, // Radius of normal points + extra to highlight it
        ],
        'circle-color': ['get', 'color'],
        'circle-opacity': 1,
        'circle-stroke-width': [
          'interpolate',
          ['linear'],
          ['zoom'], // Zoom-dependent scaling for stroke width
          startZoomLevel, // Start zoom level
          3, // Starting stroke width
          endZoomLevel, // End zoom level
          5, // Ending stroke width
        ],
        'circle-stroke-color': [
          'case',
          ['==', ['get', ScanDataEnum.point_id], currPointId],
          theme.colors.darkTheme.red, // current point id color
          ['==', ['get', ScanDataEnum.point_id], hoverPointId],
          theme.colors.highlight.mapSelectedPointRing, // hovered point id color
          theme.colors.highlight.mapSelectedPointRing, // Default (if needed)
        ],
      },
    };

    return layerStyle;
  }, [isDisabled, currPointId, hoverPointId]);

  // Fade out circle layer style for currently selected point
  const fadeOutHighlightCircleLayerStyle = useMemo(() => {
    if (isDisabled || currPointId === undefined) return null;

    const layerStyle: CircleLayer = {
      id: `${LayerEnum.heatmapFadeOutHighlight}`,
      maxzoom: MAX_ZOOM,
      type: 'circle',
      filter: ['==', ['get', ScanDataEnum.point_id], currPointId],
      paint: {
        'circle-radius': pointRadiusAtStartZoomLevel + fadeOutExtraCircleRadius, // Radius of normal points + extra to highlight it,
        'circle-radius-transition': { duration: 0 }, // Fade out transition
        'circle-color': 'transparent',
        'circle-opacity': 0,
        'circle-stroke-width': 5,
        'circle-stroke-color': [
          'case',
          ['==', ['get', ScanDataEnum.point_id], currPointId],
          theme.colors.darkTheme.blue, // current point id color
          'transparent', // Default (if needed)
        ],
        'circle-stroke-opacity': 0,
        'circle-stroke-opacity-transition': { duration: 0 }, // Fade out transition
      },
    };

    return layerStyle;
  }, [isDisabled, currPointId]);

  // Fade in and out the highlighted circle layer when currPointId changes (in this case I'm intentionally excluding currPointId from the dependency array and using updates to fadeOutHighlightCircleLayerStyle as the trigger which includes currPointId as a dependency.  This is to ensure the layer is already rendered before the animation is triggered.)
  useEffect(() => {
    if (mapRef && layerVisibility[LayerEnum.heatmap]) {
      if (ensureLayerExists(mapRef, LayerEnum.heatmapFadeOutHighlight)) {
        animatePointHighlight(currPointId, mapRef);
      }
    }
  }, [
    fadeOutHighlightCircleLayerStyle,
    mapRef,
    layerVisibility[LayerEnum.heatmap],
  ]);

  return (
    <Source
      key={LayerEnum.heatmap}
      id={LayerEnum.heatmap}
      type='geojson'
      data={pointFeatureCollection}
    >
      {heatmapLayerStyle && (
        <Layer
          key={LayerEnum.heatmap}
          {...heatmapLayerStyle}
          beforeId={ensureLayerExists(mapRef, layerBeforeId)}
        />
      )}
      {highlightedCircleLayerStyle && (
        <Layer
          key={LayerEnum.heatmapCurrentPoint}
          {...highlightedCircleLayerStyle}
          beforeId={ensureLayerExists(mapRef, layerBeforeIdCurrPoint)}
        />
      )}
      {fadeOutHighlightCircleLayerStyle && (
        <Layer
          key={LayerEnum.heatmapFadeOutHighlight}
          {...fadeOutHighlightCircleLayerStyle}
          beforeId={ensureLayerExists(mapRef, layerBeforeIdCurrPoint)}
        />
      )}
    </Source>
  );
};

export default PointDataLayers;
